import { EyeOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import type { ReactNode } from 'react';
import { usePermissionOverlay } from '@/context/PermissionOverlayContext';
import type { ModuleName, PermissionVerbAction } from '@/components/Guard';
import styles from './index.module.less';

interface PermissionBadgeProps {
  /** The module name for the permission */
  module: ModuleName;
  /** The specific permission verb action */
  verbAction?: PermissionVerbAction;
  /** Whether the current user is authorized */
  isAuthorized: boolean;
  /** The children to wrap */
  children: ReactNode;
}

/**
 * PermissionBadge wraps an element and displays permission information
 * when the permission overlay is enabled.
 *
 * Shows a colored border (green = authorized, red = unauthorized) and
 * displays the permission details on hover.
 */
export function PermissionBadge({ module, verbAction, isAuthorized, children }: PermissionBadgeProps) {
  const { enabled, showUnauthorized } = usePermissionOverlay();

  // If overlay is not enabled, just return children
  if (!enabled) {
    return <>{children}</>;
  }

  // If not authorized and we're not showing unauthorized elements, return null
  if (!isAuthorized && !showUnauthorized) {
    return null;
  }

  const permissionLabel = verbAction ? `${module}:${verbAction}` : module;
  const statusClass = isAuthorized ? styles.authorized : styles.unauthorized;

  const tooltipContent = (
    <div>
      <div style={{ fontWeight: 600 }}>{isAuthorized ? 'Authorized' : 'Unauthorized'}</div>
      <div style={{ fontSize: 11, opacity: 0.85 }}>
        <strong>Module:</strong> {module}
      </div>
      {verbAction && (
        <div style={{ fontSize: 11, opacity: 0.85 }}>
          <strong>Action:</strong> {verbAction}
        </div>
      )}
    </div>
  );

  return (
    <Tooltip title={tooltipContent} placement="top" mouseEnterDelay={0.1}>
      <span className={`${styles.permissionWrapper} ${statusClass}`}>
        <span className={`${styles.permissionBadge} ${statusClass}`}>{permissionLabel}</span>
        {children}
      </span>
    </Tooltip>
  );
}

/**
 * Floating indicator shown when the permission overlay is active.
 * Displays keyboard shortcut to dismiss.
 */
export function PermissionOverlayIndicator() {
  const { enabled } = usePermissionOverlay();

  if (!enabled) {
    return null;
  }

  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  const shortcutKey = isMac ? '⌘' : 'Ctrl';

  return (
    <div className={styles.overlayIndicator}>
      <EyeOutlined className={styles.overlayIcon} />
      <span>Permission Overlay Active</span>
      <kbd>
        {shortcutKey}+Shift+P
      </kbd>
    </div>
  );
}

export default PermissionBadge;
